import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const makeMatrix = (r, c, fill = 0) => Array.from({ length: r }, () => Array.from({ length: c }, () => fill));

const addOrSub = (A, B, sign = 1) => A.map((row, i) => row.map((v, j) => v + sign * B[i][j]));
const multiply = (A, B) => {
  const rA = A.length, cA = A[0].length, cB = B[0].length;
  const R = makeMatrix(rA, cB);
  for (let i = 0; i < rA; i++) for (let j = 0; j < cB; j++) {
    let sum = 0;
    for (let k = 0; k < cA; k++) sum += A[i][k] * B[k][j];
    R[i][j] = sum;
  }
  return R;
};
const transpose = (A) => A[0].map((_, j) => A.map(row => row[j]));
const determinant = (A) => {
  const n = A.length;
  if (n !== A[0].length) return null;
  if (n === 1) return A[0][0];
  if (n === 2) return A[0][0] * A[1][1] - A[0][1] * A[1][0];
  let det = 0;
  for (let j = 0; j < n; j++) {
    const minor = A.slice(1).map(row => row.filter((_, k) => k !== j));
    det += (j % 2 === 0 ? 1 : -1) * A[0][j] * determinant(minor);
  }
  return det;
};

const MatrixCalculator = () => {
  const [size, setSize] = useState(2);
  const [A, setA] = useState(() => [[1, 2], [3, 4]]);
  const [B, setB] = useState(() => [[5, 6], [7, 8]]);
  const [op, setOp] = useState('add');

  const handleResize = (n) => {
    setSize(n);
    setA((prev) => {
      const m = makeMatrix(n, n);
      for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) m[i][j] = prev[i]?.[j] ?? 0;
      return m;
    });
    setB((prev) => {
      const m = makeMatrix(n, n);
      for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) m[i][j] = prev[i]?.[j] ?? 0;
      return m;
    });
  };

  const setCell = (M, setM) => (i, j, v) => {
    const copy = M.map(r => [...r]);
    copy[i][j] = Number(v) || 0;
    setM(copy);
  };

  const result = useMemo(() => {
    try {
      if (op === 'add') return { label: 'A + B', value: addOrSub(A, B, 1) };
      if (op === 'sub') return { label: 'A − B', value: addOrSub(A, B, -1) };
      if (op === 'mul') return { label: 'A × B', value: multiply(A, B) };
      if (op === 'transposeA') return { label: 'Aᵀ', value: transpose(A) };
      if (op === 'detA') return { label: 'det(A)', value: [[determinant(A)]] };
    } catch { return null; }
    return null;
  }, [A, B, op]);

  return (
    <CalculatorLayout
      title="Matrix Calculator"
      subtitle="Add, subtract, multiply matrices, compute transpose and determinant."
      category="Math"
      description={<p>Perform common matrix operations on 2×2, 3×3, or 4×4 matrices. All operations are computed instantly as you type. For multiplication, both matrices must have compatible sizes.</p>}
    >
      <div className="flex items-center gap-3 mb-5">
        <label className="text-sm font-medium text-slate-700">Size:</label>
        {[2, 3, 4].map(n => (
          <Button key={n} size="sm" variant={size === n ? 'default' : 'outline'} onClick={() => handleResize(n)} className={size === n ? 'bg-saffron-500 hover:bg-saffron-600 text-white' : ''}>{n}×{n}</Button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <MatrixInput title="Matrix A" M={A} onChange={setCell(A, setA)} />
        <MatrixInput title="Matrix B" M={B} onChange={setCell(B, setB)} />
      </div>

      <Tabs value={op} onValueChange={setOp}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="add">A + B</TabsTrigger>
          <TabsTrigger value="sub">A − B</TabsTrigger>
          <TabsTrigger value="mul">A × B</TabsTrigger>
          <TabsTrigger value="transposeA">Aᵀ</TabsTrigger>
          <TabsTrigger value="detA">det(A)</TabsTrigger>
        </TabsList>
        {['add','sub','mul','transposeA','detA'].map(v => <TabsContent key={v} value={v} />)}
      </Tabs>

      {result && (
        <div className="mt-4 bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Result: {result.label}</div>
          <MatrixDisplay M={result.value} />
        </div>
      )}
    </CalculatorLayout>
  );
};

const MatrixInput = ({ title, M, onChange }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="text-sm font-display font-semibold text-bharat-navy mb-3">{title}</div>
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${M.length}, 1fr)` }}>
      {M.map((row, i) => row.map((v, j) => (
        <Input
          key={`${i}-${j}`}
          type="number"
          value={v}
          onChange={(e) => onChange(i, j, e.target.value)}
          className="text-center display-font font-semibold h-10"
        />
      )))}
    </div>
  </div>
);

const MatrixDisplay = ({ M }) => (
  <div className="inline-block bg-white rounded-lg border-2 border-saffron-300 p-3">
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${M[0].length}, minmax(60px, auto))` }}>
      {M.map((row, i) => row.map((v, j) => (
        <div key={`${i}-${j}`} className="display-font font-bold text-bharat-navy text-center px-2 py-1">
          {typeof v === 'number' ? Math.round(v * 10000) / 10000 : v}
        </div>
      )))}
    </div>
  </div>
);

export default MatrixCalculator;
